import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Building2, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Membership {
  nursery_id: string;
  nursery_name: string;
  role: string;
  is_active: boolean;
}

/**
 * Account-menu nursery switcher. Renders only when the signed-in user
 * belongs to more than one nursery (the common case is still one).
 */
export function NurserySwitcherMenu() {
  const [memberships, setMemberships] = useState<Membership[] | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc("get_my_nurseries");
      if (cancelled) return;
      if (error) {
        setMemberships([]);
        return;
      }
      setMemberships((data ?? []) as Membership[]);
    })();
    return () => { cancelled = true; };
  }, []);

  if (!memberships || memberships.length < 2) return null;

  const handleSwitch = async (m: Membership) => {
    if (m.is_active || switching) return;
    setSwitching(m.nursery_id);
    const { error } = await supabase.rpc("switch_active_nursery", {
      p_nursery_id: m.nursery_id,
    });
    if (error) {
      toast.error(error.message || "Could not switch nursery");
      setSwitching(null);
      return;
    }
    toast.success(`Switched to ${m.nursery_name}`);
    // Hard reload so all queries pick up the new active nursery
    window.location.href = "/dashboard";
  };

  return (
    <>
      <DropdownMenuLabel className="font-normal text-xs text-nursly-faint font-body">
        Switch nursery
      </DropdownMenuLabel>
      {memberships.map((m) => (
        <DropdownMenuItem
          key={m.nursery_id}
          onClick={() => handleSwitch(m)}
          disabled={!!switching}
          className="cursor-pointer font-body"
        >
          {switching === m.nursery_id ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : m.is_active ? (
            <Check className="h-4 w-4 mr-2 text-nursly-forest" />
          ) : (
            <Building2 className="h-4 w-4 mr-2" />
          )}
          <span className="truncate">{m.nursery_name}</span>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
    </>
  );
}
